import axios from 'axios'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { ApiResult, ScoreTitle } from '../../models/Enums'
import { Job } from '../../models/Job'
import {
  BlueButton,
  Card,
  GrayButton,
  OrangeButton,
} from '../Layout/CommonStyle'
import GraphBar from './Chart/GraphBar'
import MyPieChart from './Chart/MyPieChart'
import ProgressChart from './Chart/ProgressChart'
import RangeChart from './Chart/RangeChart'
import StackProgressChart from './Chart/StackProgressChart'

import SignUpForm from '../Popup/SignUpForm'
import Popup from '../Popup/Popup'
import RecommandForm from '../Popup/RecommandForm'
import session from 'redux-persist/lib/storage/session'
import { axiosGet, axiosPost } from './Axios'
import { appendRecentJob, user } from '../../store/userSlice'
import { RootState, useAppDispatch, useAppSelector } from '../../store'
import { UserRole } from '../../models/User'
import RecommandFriendForm from '../Popup/RecommandFriendForm'
import toast from 'react-hot-toast'
import { registerInventory, unregisterInventory } from './CommonApi'
import { asyncGetInventories } from '../../store/mainSlice'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import { Button } from '@mui/material'

function JobDetail() {
  const params = useParams()

  const [job, setJob] = useState<Job | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [isInInventory, setIsInInventory] = useState<boolean>(false)
  const [openRecommand, setOpenRecommand] = useState(false)
  const [showProspect, setShowProspect] = useState(false)
  const [showNeedKnowledge, setShowNeedKnowledge] = useState(false)

  const me = useAppSelector((state: RootState) => state.user.me)
  const dispatch = useAppDispatch()

  const inventories = useAppSelector(
    (state: RootState) => state.main.inventories
  )

  const scoreIndex: number[] = [1, 3, 5, 7, 9, 11, 13, 15, 17]
  const backColor: string[] = [
    '#5959ef',
    '#5959ef',
    '#5959ef',
    '#f7ac2e',
    '#f7ac2e',
    '#f7ac2e',
    '#fb7218',
    '#fb7218',
    '#fb7218',
  ]

  useEffect(() => {
    if (inventories && inventories.length === 0) {
      dispatch(asyncGetInventories())
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-extra-semi
    ;(async () => {
      setLoading(true)
      try {
        if (!params.id) {
          return
        }
        const data = await axiosGet(`/api/job/${params.id}`)

        dispatch(appendRecentJob(parseInt(params.id)))
        setJob(data)
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false)
        window.scrollTo(0, 0)
      }
    })()
  }, [params.id])

  useEffect(() => {
    if (!job || !inventories) {
      return
    }

    const inventory = inventories.find((i) => i.job.id === job.id)

    setIsInInventory(inventory !== undefined)
  }, [job, inventories])

  const handleSaveToMyInventory = async () => {
    if (!job) {
      return
    }

    await registerInventory(job.id, me.id, dispatch, () => {
      // 성공시 내부에서 처리할 것만 처리하면 된다.
      setIsInInventory(true)
    })
  }

  const handleRemoveFromMyInventory = async () => {
    if (!confirm('인벤토리에서 제거하시겠습니까?')) {
      return
    }

    if (!job) {
      return
    }

    await unregisterInventory(job.id, me.id, dispatch, () => {
      setIsInInventory(false)
    })
  }

  // Admin에서 전망의 텍스트를 수정한다.
  const onTempSaveProspect = async (job: Job) => {
    const payload = {
      jobId: job.id.toString(),
      prospect: job.prospect,
    }
    const resultJob = await axiosPost(`/api/job/temp_save_prospect`, payload)

    if (resultJob == '') {
      toast.error('저장에 실패했습니다.')
      return
    }

    toast.success('저장에 성공했습니다.')
    setJob(job)
  }

  function handleChangeProspect(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const {
      target: { value },
    } = event

    if (job != null) {
      setJob({ ...job, prospect: value })
    }
  }

  // Admin에서 필요지식의 텍스트를 수정한다.
  const onTempSaveNeedKnowledge = async (job: Job) => {
    const payload = {
      jobId: job.id.toString(),
      needKnowledge: job.needKnowledge,
    }
    const resultJob = await axiosPost(
      `/api/job/temp_save_need_knowledge`,
      payload
    )

    if (resultJob == '') {
      toast.error('저장에 실패했습니다.')
      return
    }

    toast.success('저장에 성공했습니다.')
    setJob(job)
  }

  function handleChangeNeedKnowledge(
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) {
    const {
      target: { value },
    } = event

    if (job != null) {
      setJob({ ...job, needKnowledge: value })
    }
  }

  return (
    job && (
      <Container>
        <Header>
          <img src={job.photo} className="image" />
          <div>
            <div>{job?.name}</div>
            <div>{job?.overview}</div>
            <div>하는일</div>
            <div>{job?.duties}</div>
            {me.userRole === UserRole.MENTOR && (
              <>
                <button onClick={() => setOpenRecommand(true)}>추천하기</button>
                <Popup open={openRecommand} setOpen={setOpenRecommand}>
                  <RecommandForm job={job} />
                </Popup>
              </>
            )}
            {me.userRole === UserRole.MENTEE && (
              <Buttons>
                <OrangeButton onClick={() => setOpenRecommand(true)}>
                  친구에게 추천하기
                </OrangeButton>

                {isInInventory ? (
                  <BlueButton onClick={() => handleRemoveFromMyInventory()}>
                    <StarIcon /> 인벤토리에 추가됨
                  </BlueButton>
                ) : (
                  <GrayButton onClick={() => handleSaveToMyInventory()}>
                    <StarBorderIcon />내 인벤토리에 담기
                  </GrayButton>
                )}

                <Popup open={openRecommand} setOpen={setOpenRecommand}>
                  <RecommandFriendForm job={job} setOpen={setOpenRecommand} />
                </Popup>
              </Buttons>
            )}
          </div>
        </Header>
        <Value>
          <div>임금</div>
          <div>
            {job?.payLower.toLocaleString()}만원 ~{' '}
            {job?.payUpper.toLocaleString()}만원
          </div>
        </Value>
        <RangeChart
          minRange={parseInt(process.env.REACT_APP_PAY_MIN ?? '1000')}
          maxRange={parseInt(process.env.REACT_APP_PAY_MAX ?? '17000')}
          minValue={job.payLower}
          maxValue={job.payUpper}
          color="tomato"
          height={10}
        />
        <Value>
          <div>직업만족도</div>
          <div>{job?.satisfaction}%</div>
        </Value>
        <ProgressChart
          barColor="tomato"
          value={job.satisfaction}
          unit="%"
          align="center"
          minValue={0}
          maxValue={100}
          height={35}
          border={false}
        />
        <Value>
          <div>전망</div>
          <div></div>
        </Value>
        <StackProgressChart
          values={{
            감소: job.prospectDecrease,
            다소감소: job.prospectDecreaseLittle,
            유지: job.prospectKeep,
            다소증가: job.prospectIncreaseLittle,
            증가: job.prospectIncrease,
          }}
          names={['감소', '다소감소', '유지', '다소증가', '증가']}
          unit="%"
          align="center"
          height={50}
        />
        <Description>{job.prospect}</Description>
        {me.userRole === UserRole.ADMIN && (
          <>
            <Button onClick={() => setShowProspect(!showProspect)}>편집</Button>
            <Description style={{ display: showProspect ? 'block' : 'none' }}>
              <textarea
                value={job.prospect}
                onChange={handleChangeProspect}
              ></textarea>
              <BlueButton onClick={() => onTempSaveProspect(job)}>
                {' '}
                저장{' '}
              </BlueButton>
            </Description>
          </>
        )}
        <ScoreContainer>
          {scoreIndex.map((i, index) => (
            <Score key={index}>
              <div>{ScoreTitle[i]}</div>
              <GraphBar
                score={job.scores[i * 5]}
                backColor={backColor[index]}
              />
              <GraphBar
                score={job.scores[i * 5 + 1]}
                backColor={backColor[index]}
              />
              <GraphBar
                score={job.scores[i * 5 + 2]}
                backColor={backColor[index]}
              />
              <GraphBar
                score={job.scores[i * 5 + 3]}
                backColor={backColor[index]}
              />
              <GraphBar
                score={job.scores[i * 5 + 4]}
                backColor={backColor[index]}
              />
            </Score>
          ))}
        </ScoreContainer>
        <Title>필요지식</Title>
        <Description>{job.needKnowledge}</Description>
        {me.userRole === UserRole.ADMIN && (
          <>
            <Button onClick={() => setShowNeedKnowledge(!showNeedKnowledge)}>
              편집
            </Button>
            <Description
              style={{ display: showNeedKnowledge ? 'block' : 'none' }}
            >
              <textarea
                value={job.needKnowledge}
                onChange={handleChangeNeedKnowledge}
              ></textarea>
              <BlueButton onClick={() => onTempSaveNeedKnowledge(job)}>
                {' '}
                저장{' '}
              </BlueButton>
            </Description>
          </>
        )}
        <EduContainer>
          <PieContainer>
            <div>학력분포</div>
            <MyPieChart
              values={[
                { name: '중줄이하', value: job.educationMiddle },
                { name: '고졸', value: job.educationHigh },
                { name: '전문대졸', value: job.educationJuniorCollage },
                { name: '대졸', value: job.educationCollage },
                { name: '대학원졸', value: job.educationGraduate },
                { name: '박사', value: job.educationDoctor },
              ]}
              colors={[
                '#0088FE',
                '#00C49F',
                '#FFBB28',
                '#FF8042',
                '#63920c',
                '#f76587',
              ]}
            />
          </PieContainer>
          <PieContainer>
            <div>전공학과</div>
            <MyPieChart
              values={[
                { name: '인문계열', value: job.majorHumanities },
                { name: '사회계열', value: job.majorSocial },
                { name: '교육계열', value: job.majorEducation },
                { name: '공학계열', value: job.majorEngineering },
                { name: '자연계열', value: job.majorNatuaral },
                { name: '의학계열', value: job.majorMedicine },
                { name: '예체능계열', value: job.majorArt },
              ]}
              colors={[
                '#0088FE',
                '#00C49F',
                '#FFBB28',
                '#FF8042',
                '#63920c',
                '#f76587',
                '#801b1b',
              ]}
            />
          </PieContainer>
        </EduContainer>

        <Value>
          <div>관련학과</div>
          <div>
            {job.relatedMajors &&
              job.relatedMajors.length > 0 &&
              job.relatedMajors.map((r) => {
                return (
                  <a href={r.majorCode.url} target="_blank" key={r.id}>
                    <span>{r.majorCode.name}</span>
                  </a>
                )
              })}
          </div>
        </Value>
        <Value>
          <div>관련정보처</div>
          <div>
            {job.relatedInfoOrgans &&
              job.relatedInfoOrgans.length > 0 &&
              job.relatedInfoOrgans.map((r) => {
                return (
                  <a href={r.infoOrganCode.url} target="_blank" key={r.id}>
                    <span>{r.infoOrganCode.name}</span>
                  </a>
                )
              })}
          </div>
        </Value>

        <Title>관련직업</Title>
        <JobContainer key={0}>
          {job.relatedJobs.map((relatejob, index) => (
            <Link to={'/job/' + relatejob.job.id} key={index}>
              <Job2>
                <img
                  //src={process.env.REACT_APP_API_SERVER + relatejob.job.photo}
                  src={relatejob.job.photo}
                />
                <div>{relatejob.job.name} </div>
              </Job2>
            </Link>
          ))}
        </JobContainer>
        <Title>전직가능한 직업</Title>
        <JobContainer key={1}>
          {job.changeableJobs.map((changableJob, index) => (
            <Link to={'/job/' + changableJob.job.id} key={index}>
              <Job2>
                <img src={changableJob.job.photo} />
                <div>{changableJob.job.name} </div>
              </Job2>
            </Link>
          ))}
        </JobContainer>
      </Container>
    )
  )
}

const Container = styled(Card)`
  text-align: left;
  width: 1100px;
  margin: 0 auto;
  padding: 50px 30px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    padding: 50px 20px;
    margin-bottom: 100px;
  }
`

const Header = styled.div`
  display: flex;
  column-gap: 40px;
  padding-bottom: 20px;
  margin-bottom: 20px;
  border-bottom: 1px solid #ddd;

  @media (max-width: 768px) {
    display: block;
    margin-bottom: 0;
  }

  img {
    width: 300px;
    height: 300px;

    @media (max-width: 768px) {
      width: 100%;
      //height: 100%;
    }
  }
  div {
    line-height: 1.6;
    div:nth-child(1) {
      font-weight: 600;
      font-size: 1.5rem;
      padding-bottom: 1.2rem;
    }
    div:nth-child(2) {
      padding-bottom: 1rem;
    }
    div:nth-child(3) {
      font-weight: 600;
      font-size: 1.1rem;
      padding-bottom: 0.5rem;
    }
  }
`

const Buttons = styled.div`
  display: flex;
  column-gap: 10px;
  align-items: center;

  & > button:nth-child(2) {
    position: relative;
    padding-left: 40px;
    svg {
      position: absolute;
      font-size: 1.6rem;
      color: white;
      border: 1px;
      top: 5px;
      left: 10px;
    }
  }
`

// const InInventory = styled.button`
//   border: 1px solid #ddd;
//   background-color: white;
//`

const Value = styled.div`
  display: flex;
  column-gap: 40px;
  padding-top: 40px;
  align-items: baseline;

  @media (max-width: 768px) {
    display: block;
  }

  div:first-child {
    font-weight: 500;
    min-width: 80px;

    @media (max-width: 768px) {
      padding: 20px 0;
    }
  }

  div:nth-child(2) {
    line-height: 1.8;

    a span {
      text-decoration: none;
      color: #555;
      margin-right: 20px;
    }
  }
`
const Title = styled.div`
  font-weight: 500;
  padding: 40px 0 20px;

  @media (max-width: 768px) {
  }
`

const Description = styled.div`
  padding: 10px 0 30px;
  line-height: 1.7;
  white-space: pre-line;

  textarea {
    width: 100%;
    height: 200px;
  }
`

const ScoreContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: 1fr 1fr 1fr;
  grid-row-gap: 40px;
  grid-column-gap: 20px;
  padding-top: 40px;

  @media (max-width: 768px) {
    display: block;
  }
`
const Score = styled.div`
  & > div:first-child {
    font-weight: 500;
    padding: 20px 0 10px;
  }
  & > div:nth-child(2) {
  }
`

const EduContainer = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 60px 20px;

  @media (max-width: 768px) {
    display: block;
    padding: 0;
  }
`
const PieContainer = styled.div`
  width: 400px;
  height: 400px;
  background-color: #fff;

  @media (max-width: 768px) {
    margin: 0 auto;
  }

  div {
    font-weight: 500;
  }
`

const JobContainer = styled.div`
  display: flex;
  padding: 10px 0;
  //border: 1px solid #ddd;
  gap: 30px;
  flex-wrap: wrap;

  div {
    font-weight: 500;
  }
`

const Job2 = styled.div`
  text-align: center;
  color: #333;
  width: 200px;

  img {
    width: 200px;
  }
`
export default JobDetail
